import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { Button } from './ui/Button';
import { Textarea } from './ui/Textarea';
import { Select } from './ui/Select';
import ShareDialog from './ShareDialog';
import Comments from './Comments';
import VersionHistory from './VersionHistory';
import ChatWidget from './ChatWidget';
import DetachableEditor from './DetachableEditor';
import QuickFillButton from './QuickFillButton';
import DocumentSummarizer from './DocumentSummarizer';
import { getSuggestion, formatDocument } from '../lib/aiService';
import { profileCardService } from '../lib/profileCardService';
import {
  Save,
  Share,
  Download,
  MessageSquare,
  History,
  Type,
  List,
  Hash,
  Quote,
  Wand2,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileText,
  Eye,
  X,
  Undo,
  Redo,
  Upload,
  Crown
} from 'lucide-react';
import { saveAs } from 'file-saver';
import html2pdf from 'html2pdf.js';
import { extractTemplateContent } from '../utils/templateUtils';
import { useSubscription } from '../hooks/useSubscription';
import { AIFeatureGate } from './FeatureGate';
import { trackAnalyticsEvent } from '../lib/analytics';
import { useTranslation } from '../lib/i18n';

interface Template {
  id: string;
  name: string;
  description: string;
  content: { text: string };
}

interface Version {
  id: string;
  title: string;
  content: { text: string } | null;
  created_at: string;
  created_by: string;
}

const DocumentEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const templateId = searchParams.get('template');
  const { user } = useAuthStore();
  const { canUseAIFeature, subscription } = useSubscription();
  const { t } = useTranslation();

  const [title, setTitle] = useState(t('editor.untitledDocument'));
  const [content, setContent] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [showSummarizer, setShowSummarizer] = useState(false);
  const [documentExists, setDocumentExists] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: string }>({ text: '', type: '' });

  // Undo/Redo functionality
  const [undoStack, setUndoStack] = useState<Array<{title: string, content: string}>>([]);
  const [redoStack, setRedoStack] = useState<Array<{title: string, content: string}>>([]);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);
  const [isRemoteUpdate, setIsRemoteUpdate] = useState(false);

  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const hasUnsavedChanges = useRef(false);

  // Debug logging function
  const debugLog = (message: string, data?: any) => {
    console.log(`[DocumentEditor] ${message}`, data || '');
  };

  // Load document data
  const loadDocument = useCallback(async (documentId: string) => {
    if (!user?.id) {
      debugLog('No user found, cannot load document');
      setLoadError(t('editor.signInToLoad'));
      setIsLoadingDocument(false);
      return;
    }

    try {
      setIsLoadingDocument(true);
      setLoadError(null);
      debugLog(`Loading document with ID: ${documentId}`);
      debugLog(`Current user ID: ${user.id}`);

      // First, verify the user session is valid
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        debugLog('Session error:', sessionError);
        throw new Error(t('editor.sessionInvalid'));
      }

      if (!session) {
        debugLog('No active session found');
        throw new Error(t('editor.noSession'));
      }

      debugLog('Session valid, proceeding with document query');

      // Query the document with explicit user ownership check
      const { data: document, error: docError } = await supabase
        .from('documents')
        .select('id, title, content, content->>text as content_text, user_id, created_at, updated_at')
        .eq('id', documentId)
        .limit(1);

      debugLog('Document query completed');
      debugLog('Document data received:', document);
      debugLog('Document error:', docError);

      if (docError) {
        debugLog('Document query error:', docError);
        
        if (docError.code === 'PGRST116') {
          throw new Error(t('editor.docNotFound'));
        } else if (docError.message?.includes('JWT')) {
          throw new Error(t('editor.authError'));
        } else if (docError.message?.includes('permission')) {
          throw new Error(t('editor.noPermission'));
        } else {
          throw new Error(t('editor.failedLoadDocMsg', { error: docError.message }));
        }
      }

      if (!document || !Array.isArray(document) || document.length === 0) {
        debugLog('No document returned from query');
        throw new Error(t('editor.docNotFound'));
      }

      const doc = document[0];

      debugLog('Document loaded successfully:', {
        id: doc.id,
        title: doc.title,
        hasContent: !!doc.content,
        contentType: typeof doc.content,
        contentKeys: doc.content ? Object.keys(doc.content) : 'no content',
        hasContentText: !!doc.content_text,
        contentTextLength: doc.content_text ? doc.content_text.length : 0
      });

      // Extract content with multiple fallback strategies
      let documentContent = '';
      
      // First try the direct text extraction from content->>text
      if (doc.content_text) {
        documentContent = doc.content_text;
        debugLog('Content extracted from content->>text operator');
      } else if (doc.content) {
        if (typeof doc.content === 'object' && doc.content.text) {
          documentContent = doc.content.text;
          debugLog('Content extracted from content.text');
        } else if (typeof doc.content === 'string') {
          documentContent = doc.content;
          debugLog('Content extracted as string');
        } else if (typeof doc.content === 'object') {
          // Try other possible keys
          const possibleKeys = ['content', 'body', 'data', 'value'];
          for (const key of possibleKeys) {
            if (doc.content[key]) {
              documentContent = String(doc.content[key]);
              debugLog(`Content extracted from content.${key}`);
              break;
            }
          }
          
          if (!documentContent) {
            // Last resort: stringify the object
            documentContent = JSON.stringify(doc.content, null, 2);
            debugLog('Content extracted by stringifying object');
          }
        }
      }

      debugLog('Final content length:', documentContent.length);
      debugLog('Final content preview:', documentContent.substring(0, 100) + '...');

      setTitle(doc.title || 'Untitled Document');
      setContent(documentContent);
      setDocumentExists(true);
      setLastSaved(new Date(doc.updated_at));
      setSaveStatus('saved');

      debugLog('Document state updated successfully');

      await trackAnalyticsEvent({
        documentId: doc.id,
        eventType: 'view',
      });

    } catch (err: any) {
      debugLog('Error loading document:', err);
      console.error('Document loading error:', err);
      setLoadError(err.message || t('editor.failedLoadDoc'));
      setDocumentExists(false);
    } finally {
      setIsLoadingDocument(false);
    }
  }, [user?.id]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      debugLog('Loading templates...');
      
      const { data: templatesData, error: templatesError } = await supabase
        .from('templates')
        .select('id, name, description, content')
        .order('name');

      if (templatesError) {
        debugLog('Templates error:', templatesError);
        console.error('Error loading templates:', templatesError);
        return;
      }

      debugLog('Templates loaded:', templatesData?.length || 0);
      setTemplates(templatesData || []);
    } catch (error) {
      debugLog('Templates loading error:', error);
      console.error('Error loading templates:', error);
    }
  }, []);

  // Load template content
  const loadTemplate = useCallback(async (templateId: string) => {
    try {
      debugLog(`Loading template with ID: ${templateId}`);
      
      const { data: template, error: templateError } = await supabase
        .from('templates')
        .select('id, name, description, content')
        .eq('id', templateId)
        .single();

      if (templateError) {
        debugLog('Template error:', templateError);
        throw templateError;
      }

      if (template) {
        console.log('=== TEMPLATE LOADED FROM DB (URL PARAM) ===');
        console.log('Template ID:', template.id);
        console.log('Template name:', template.name);
        console.log('Raw template object:', template);
        console.log('Template content:', template.content);
        console.log('Content type:', typeof template.content);
        console.log('Content constructor:', template.content?.constructor?.name);
        
        setTitle(template.name || 'Untitled Document');
        
        // Use centralized content extraction
        let templateContent = extractTemplateContent(template.content);
        
        console.log('=== URL TEMPLATE EXTRACTION RESULT ===');
        console.log('Final content length:', templateContent.length);
        console.log('Content preview:', templateContent.substring(0, 200));
        
        if (!templateContent) {
          console.warn('⚠️ URL Template: No content extracted, using empty document');
          templateContent = '';
        }
        
        setContent(templateContent);
        setDocumentExists(false); // This is a new document from template
        setSaveStatus(null);
        
        // Clear any existing errors
        setError(null);
        setLoadError(null);
        
        console.log('✅ URL Template loaded into editor (content length:', templateContent.length, ')');
      }
    } catch (error) {
      debugLog('Template loading error:', error);
      console.error('Error loading template:', error);
      setLoadError(t('editor.failedLoadTemplate', { error: error.message || t('editor.unknownError') }));
    }
  }, []);

  // Initialize document
  useEffect(() => {
    debugLog('DocumentEditor mounted', { id, templateId, user: !!user });
    
    if (!user) {
      debugLog('No user, redirecting to auth');
      navigate('/auth');
      return;
    }

    const initializeDocument = async () => {
      try {
        // Load templates first
        await loadTemplates();

        // Check if template content was passed via navigation state
        if (location.state?.templateContent !== undefined || location.state?.templateTitle) {
          debugLog('Loading template from navigation state');
          let templateContent = location.state.templateContent || '';
          const templateTitle = location.state.templateTitle || 'Untitled Document';
          
          // If templateContent is an object, extract the text
          if (typeof templateContent === 'object' && templateContent !== null) {
            templateContent = extractTemplateContent(templateContent);
          }
          
          debugLog('Template content extracted:', { 
            title: templateTitle, 
            contentLength: templateContent.length,
            contentType: typeof templateContent,
            contentPreview: templateContent.substring(0, 100)
          });
          
          setTitle(templateTitle);
          setContent(templateContent);
          setDocumentExists(false);
          setSaveStatus(null);
          setError(null);
          setLoadError(null);
          setIsLoadingDocument(false);
          
          // Clear the navigation state to prevent reloading on subsequent renders
          window.history.replaceState({}, '', window.location.pathname);
        } else if (id) {
          // Load existing document
          debugLog(`Loading existing document: ${id}`);
          await loadDocument(id);
        } else if (templateId) {
          // Load template
          debugLog(`Loading template: ${templateId}`);
          await loadTemplate(templateId);
        } else {
          // New document
          debugLog('Creating new document');
          setIsLoadingDocument(false);
          setDocumentExists(false);
          setSaveStatus(null);
        }
      } catch (error) {
        debugLog('Initialization error:', error);
        console.error('Error initializing document:', error);
        setError(t('editor.failedInit'));
        setIsLoadingDocument(false);
      }
    };

    initializeDocument();
  }, [id, templateId, user, navigate, loadDocument, loadTemplate, loadTemplates, location.state]);

  // Auto-save functionality
  useEffect(() => {
    if (!documentExists || !user || saving) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (hasUnsavedChanges.current) {
        handleSave();
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [title, content, documentExists, user, saving]);

  // Track unsaved changes
  useEffect(() => {
    // Don't track changes during undo/redo operations
    if (isUndoRedoAction || isRemoteUpdate) {
      setIsUndoRedoAction(false);
      setIsRemoteUpdate(false);
      return;
    }
    
    hasUnsavedChanges.current = true;
    if (saveStatus === 'saved') {
      setSaveStatus(null);
    }
    
    // Add current state to undo stack (debounced)
    const timeoutId = setTimeout(() => {
      setUndoStack(prev => {
        const newStack = [...prev, { title, content }];
        // Keep only last 50 states to prevent memory issues
        return newStack.slice(-50);
      });
      // Clear redo stack when new changes are made
      setRedoStack([]);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [title, content]);

  // Real-time document updates subscription
  useEffect(() => {
    if (!id || !user || !documentExists) return;

    debugLog('Setting up real-time subscription for document:', id);

    const channel = supabase
      .channel(`document:${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'documents',
          filter: `id=eq.${id}`
        },
        (payload) => {
          debugLog('Real-time document update received:', payload);
          debugLog('Full payload.new object:', payload.new);
          
          // Apply all updates to ensure real-time sync
          debugLog('Applying real-time document update');
          
          setIsRemoteUpdate(true);
          
          // Update title if changed
          if (payload.new?.title && payload.new.title !== title) {
            setTitle(payload.new.title);
            debugLog('Title updated from real-time:', payload.new.title);
          }
          
          // Update content if changed
          if (payload.new?.content?.text && payload.new.content.text !== content) {
            setContent(payload.new.content.text);
            debugLog('Content updated from real-time, length:', payload.new.content.text.length);
          }
          
          // Update save status
          if (payload.new?.updated_at) {
            setLastSaved(new Date(payload.new.updated_at));
            setSaveStatus('saved');
            hasUnsavedChanges.current = false;
          }
          
          debugLog('Real-time update applied successfully');
        }
      )
      .subscribe((status) => {
        debugLog('Real-time subscription status:', status);
        if (status === 'SUBSCRIBED') {
          debugLog('Successfully subscribed to document updates');
        } else if (status === 'CHANNEL_ERROR') {
          debugLog('Channel error in real-time subscription');
        }
      });

    return () => {
      debugLog('Cleaning up real-time subscription');
      channel.unsubscribe();
    };
  }, [id, user, documentExists]);

  const handleSave = async () => {
    if (!user) {
      setError(t('editor.signInToSave'));
      return;
    }

    try {
      setSaving(true);
      setSaveStatus('saving');
      setError(null);
      debugLog('Saving document...', { id, title, contentLength: content.length });
      debugLog('Save decision variables:', { 
        id, 
        documentExists, 
        userId: user.id,
        hasId: !!id,
        willUpdate: !!(id && documentExists),
        willCreate: !(id && documentExists)
      });

      const documentData = {
        title: title || 'Untitled Document',
        content: { text: content },
        user_id: user.id,
        updated_at: new Date().toISOString()
      };

      if (id && documentExists) {
        // Update existing document
        debugLog('Updating existing document');
        const { data, error: updateError } = await supabase
          .from('documents')
          .update(documentData)
          .eq('id', id)
          .eq('user_id', user.id)
          .select()
          .single();

        if (updateError) {
          debugLog('Update error:', updateError);
          debugLog('Full updateError object:', JSON.stringify(updateError, null, 2));
          throw updateError;
        }

        debugLog('Document updated successfully:', data);
      } else {
        // Create new document
        debugLog('Creating new document');
        const { data, error: insertError } = await supabase
          .from('documents')
          .insert([documentData])
          .select()
          .single();

        if (insertError) {
          debugLog('Insert error:', insertError);
          throw insertError;
        }

        debugLog('Document created successfully:', data);
        
        if (data?.id) {
          setDocumentExists(true);
          // Update URL to include the new document ID
          navigate(`/editor/${data.id}`, { replace: true });
        }
      }

      setSaveStatus('saved');
      setLastSaved(new Date());
      hasUnsavedChanges.current = false;
      debugLog('Save completed successfully');

    } catch (err: any) {
      debugLog('Save error:', err);
      console.error('Error saving document:', err);
      setError(err.message || t('editor.failedSave'));
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleFormat = async () => {
    // Check subscription level before processing
    const canUseBasic = canUseAIFeature('basic');
    console.log('🤖 Format feature check:', {
      canUseBasic,
      userPlan: subscription?.plan,
      aiLevel: subscription?.features.aiProcessing
    });
    
    if (!canUseBasic) {
      setAiError(t('editor.formatRequiresBasic'));
      return;
    }

    if (!content.trim()) {
      setAiError(t('editor.addContentFormat'));
      return;
    }

    setIsFormatting(true);
    setAiError(null);

    try {
      const result = await formatDocument(content);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      if (result.formattedText) {
        setContent(result.formattedText);
      }
    } catch (error: any) {
      console.error('Error formatting document:', error);
      setAiError(error.message || t('editor.failedFormat'));
    } finally {
      setIsFormatting(false);
    }
  };

  const handleImprove = async () => {
    // Check subscription level before processing
    const canUseAdvanced = canUseAIFeature('advanced');
    console.log('🤖 Improve feature check:', {
      canUseAdvanced,
      userPlan: subscription?.plan,
      aiLevel: subscription?.features.aiProcessing
    });
    
    if (!canUseAdvanced) {
      setAiError(t('editor.improveRequiresAI'));
      return;
    }

    if (!content.trim()) {
      setAiError(t('editor.addContentImprove'));
      return;
    }

    setIsImproving(true);
    setAiError(null);

    try {
      const result = await getSuggestion(content);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      if (result.suggestion) {
        setContent(result.suggestion);
      }
    } catch (error: any) {
      console.error('Error improving document:', error);
      setAiError(error.message || t('editor.failedImprove'));
    } finally {
      setIsImproving(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx' | 'txt' | 'md' | 'json' | 'html' | 'xml' | 'rtf') => {
    try {
      const fileName = `${title || 'document'}.${format}`;

      switch (format) {
        case 'txt':
          const txtBlob = new Blob([content], { type: 'text/plain' });
          saveAs(txtBlob, fileName);
          break;

        case 'md':
          const mdBlob = new Blob([content], { type: 'text/markdown' });
          saveAs(mdBlob, fileName);
          break;

        case 'json':
          const jsonData = { title, content, exported_at: new Date().toISOString() };
          const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
          saveAs(jsonBlob, fileName);
          break;

        case 'pdf':
          const element = document.createElement('div');
          element.innerHTML = `
            <div style="
              background-color: #ffffff;
              color: #000000;
              padding: 40px;
              font-family: Arial, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            ">
              <h1 style="
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 20px;
                color: #000000;
                line-height: 1.2;
              ">${title}</h1>
              <div style="
                white-space: pre-wrap;
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #000000;
                font-size: 12px;
                word-wrap: break-word;
              ">${content}</div>
            </div>
          `;

          const opt = {
            margin: 0.5,
            filename: fileName,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              letterRendering: true,
              backgroundColor: '#ffffff'
            },
            jsPDF: {
              unit: 'in',
              format: 'letter',
              orientation: 'portrait',
              compress: true
            }
          };

          await html2pdf().set(opt).from(element).save();
          break;

        case 'html':
          const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      line-height: 1.6;
      color: #333;
    }
    h1 {
      font-size: 28px;
      margin-bottom: 20px;
      color: #222;
    }
    pre {
      white-space: pre-wrap;
      word-wrap: break-word;
      background: #f5f5f5;
      padding: 15px;
      border-radius: 5px;
      font-family: 'Courier New', monospace;
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <pre>${content.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c))}</pre>
</body>
</html>`;
          const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
          saveAs(htmlBlob, fileName);
          break;

        case 'xml':
          const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<document>
  <metadata>
    <title>${title.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c))}</title>
    <exported>${new Date().toISOString()}</exported>
  </metadata>
  <content><![CDATA[${content}]]></content>
</document>`;
          const xmlBlob = new Blob([xmlContent], { type: 'application/xml' });
          saveAs(xmlBlob, fileName);
          break;

        case 'rtf':
          const rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0 Times New Roman;}{\\f1 Courier New;}}
{\\colortbl;\\red0\\green0\\blue0;}
\\f0\\fs28\\b ${title.replace(/[\\{}]/g, '\\$&')}\\b0\\fs24\\par
\\par
\\f1 ${content.replace(/\n/g, '\\par ').replace(/[\\{}]/g, '\\$&')}
}`;
          const rtfBlob = new Blob([rtfContent], { type: 'application/rtf' });
          saveAs(rtfBlob, fileName);
          break;

        default:
          throw new Error(t('editor.exportNotSupported', { format }));
      }

      if (id) {
        await trackAnalyticsEvent({
          documentId: id,
          eventType: 'export',
          exportFormat: format,
        });
      }
    } catch (error) {
      console.error('Error exporting document:', error);
      setError(t('editor.failedExport'));
    }
  };

  const addFormatting = (type: 'heading' | 'bullet' | 'number' | 'quote') => {
    const textarea = document.getElementById('main-document-editor') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const beforeText = content.substring(0, start);
    const afterText = content.substring(end);

    let newText = '';
    let newCursorPos = start;

    switch (type) {
      case 'heading':
        if (selectedText) {
          newText = `${beforeText}═══════════════════════════════════════════════════════════════\n                        ${selectedText.toUpperCase()}\n═══════════════════════════════════════════════════════════════\n\n${afterText}`;
          newCursorPos = start + selectedText.length + 130;
        } else {
          const headingText = '═══════════════════════════════════════════════════════════════\n                        YOUR HEADING HERE\n═══════════════════════════════════════════════════════════════\n\n';
          newText = beforeText + headingText + afterText;
          newCursorPos = start + 88;
        }
        break;
        
      case 'bullet':
        if (selectedText) {
          const lines = selectedText.split('\n');
          const bulletLines = lines.map(line => line.trim() ? `• ${line.trim()}` : line).join('\n');
          newText = beforeText + bulletLines + afterText;
          newCursorPos = end + (lines.length * 2);
        } else {
          newText = beforeText + '• ' + afterText;
          newCursorPos = start + 2;
        }
        break;
        
      case 'number':
        if (selectedText) {
          const lines = selectedText.split('\n').filter(line => line.trim());
          const numberedLines = lines.map((line, index) => `${index + 1}. ${line.trim()}`).join('\n');
          newText = beforeText + numberedLines + afterText;
          newCursorPos = end + (lines.length * 3);
        } else {
          newText = beforeText + '1. ' + afterText;
          newCursorPos = start + 3;
        }
        break;
        
      case 'quote':
        if (selectedText) {
          const lines = selectedText.split('\n');
          const quotedLines = lines.map(line => line.trim() ? `> ${line.trim()}` : line).join('\n');
          newText = beforeText + quotedLines + afterText;
          newCursorPos = end + (lines.length * 2);
        } else {
          newText = beforeText + '> ' + afterText;
          newCursorPos = start + 2;
        }
        break;
    }

    setContent(newText);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleRestoreVersion = (version: Version) => {
    if (version.content?.text) {
      setTitle(version.title);
      setContent(version.content.text);
      setShowVersionHistory(false);
      handleSave();
    }
  };

  const handleViewVersion = (version: Version) => {
    // For now, just show an alert with version info
    alert(t('editor.versionAlert', {
      date: new Date(version.created_at).toLocaleString(),
      title: version.title,
      preview: version.content?.text?.substring(0, 200)
    }));
  };

  const handleApplySuggestion = (suggestion: string) => {
    setContent(suggestion);
    setShowChatWidget(false);
  };
  
  const handleUndo = () => {
    if (undoStack.length === 0) return;
    
    const currentState = { title, content };
    const previousState = undoStack[undoStack.length - 1];
    
    setIsUndoRedoAction(true);
    setTitle(previousState.title);
    setContent(previousState.content);
    
    setRedoStack(prev => [...prev, currentState]);
    setUndoStack(prev => prev.slice(0, -1));
  };
  
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    
    const currentState = { title, content };
    const nextState = redoStack[redoStack.length - 1];
    
    setIsUndoRedoAction(true);
    setTitle(nextState.title);
    setContent(nextState.content);
    
    setUndoStack(prev => [...prev, currentState]);
    setRedoStack(prev => prev.slice(0, -1));
  };

  const clearError = () => {
    setError(null);
  };

  const clearAiError = () => {
    setAiError(null);
  };

  const handleQuickFill = (formattedText: string) => {
    const textarea = document.getElementById('main-document-editor') as HTMLTextAreaElement;

    if (!textarea) {
      setContent(prev => {
        const currentContent = prev.trim();
        return currentContent ? currentContent + '\n\n' + formattedText : formattedText;
      });
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const beforeText = content.substring(0, start);
    const afterText = content.substring(end);

    const separator = beforeText && !beforeText.endsWith('\n') ? '\n\n' : '';
    const newText = beforeText + separator + formattedText + afterText;

    setContent(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = (beforeText + separator + formattedText).length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const handleQuickFillReplace = (card: any) => {
    const result = profileCardService.replacePlaceholders(content, card);

    setContent(result.content);

    if (result.replacedCount > 0) {
      setMessage({
        text: result.message,
        type: 'success'
      });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
    } else {
      setMessage({
        text: 'No placeholders found to replace. Tip: Use [Your Full Name], [Phone Number], [Email], or [Location] in your document.',
        type: 'info'
      });

      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <FileText className="w-16 h-16 text-blue-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t('editor.pleaseSignIn')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t('editor.pleaseSignInMessage')}
          </p>
          <Button onClick={() => navigate('/auth')} className="w-full">
            {t('editor.signInToContinue')}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoadingDocument) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8">
          <Loader2 className="w-12 h-12 text-blue-500 mx-auto animate-spin" />
          <h2 className="text-2xl font-bold">{t('editor.loadingDocument')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t('editor.loadingDocumentMessage')}
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400">{t('editor.failedLoad')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {loadError}
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/documents')} variant="outline">
              {t('editor.backToDocuments')}
            </Button>
            <Button onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('editor.documentTitle')}
                className="text-2xl font-bold bg-transparent border-none focus:outline-none focus:ring-0 w-full text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={loading}
              />
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center">
                  {saveStatus === 'saving' && (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      {t('editor.saving')}
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                      {lastSaved ? t('editor.savedAt', { time: lastSaved.toLocaleTimeString() }) : t('editor.saved')}
                    </>
                  )}
                  {saveStatus === 'error' && (
                    <>
                      <AlertCircle className="w-4 h-4 mr-1 text-red-500" />
                      {t('editor.saveFailed')}
                    </>
                  )}
                  {!saveStatus && hasUnsavedChanges.current && (
                    <>
                      <AlertCircle className="w-4 h-4 mr-1 text-yellow-500" />
                      {t('editor.unsavedChanges')}
                    </>
                  )}
                </span>
                <span>{t('editor.characters', { count: content.length })}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* History Controls */}
              <div className="flex items-center space-x-1 border-r border-gray-300 dark:border-gray-600 pr-3">
                <Button
                  onClick={handleUndo}
                  disabled={undoStack.length === 0 || loading}
                  variant="outline"
                  size="sm"
                  title={t('editor.undo')}
                  className="font-bold"
                >
                  <Undo className="w-4 h-4" />
                </Button>
                
                <Button
                  onClick={handleRedo}
                  disabled={redoStack.length === 0 || loading}
                  variant="outline"
                  size="sm"
                  title={t('editor.redo')}
                  className="font-bold"
                >
                  <Redo className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Primary Actions */}
              <Button
                onClick={handleSave}
                disabled={saving || loading}
                size="sm"
                className="font-bold"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('common.save')}
                  </>
                )}
              </Button>
              
              {documentExists && (
                <Button
                  onClick={handleSave}
                  disabled={saving || loading}
                  variant="outline"
                  size="sm"
                  className="font-bold"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {t('editor.updateDocument')}
                    </>
                  )}
                </Button>
              )}
              
              {documentExists && (
                <Button
                  onClick={() => setShowShareDialog(true)}
                  variant="outline"
                  size="sm"
                  className="font-bold"
                >
                  <Share className="w-4 h-4 mr-2" />
                  {t('editor.share')}
                </Button>
              )}
              
              <div className="relative">
                <Button
                  onClick={() => {
                    const exportMenu = document.getElementById('export-menu');
                    if (exportMenu) {
                      exportMenu.classList.toggle('hidden');
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="font-bold"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('editor.export')}
                </Button>
                <div
                  id="export-menu"
                  className="hidden absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700"
                >
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('pdf')}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.pdf')}</span>
                    </button>
                    <button
                      onClick={() => handleExport('txt')}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.txt')}</span>
                    </button>
                    <button
                      onClick={() => handleExport('md')}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.md')}</span>
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.json')}</span>
                    </button>
                    <button
                      onClick={() => {
                        const available = subscription?.features.exportFormats?.includes('html');
                        if (!available) {
                          alert(t('editor.htmlExportRequiresAI'));
                          navigate('/pricing');
                        } else {
                          handleExport('html');
                        }
                      }}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.html')}</span>
                      {!subscription?.features.exportFormats?.includes('html') && (
                        <Crown className="w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        const available = subscription?.features.exportFormats?.includes('xml');
                        if (!available) {
                          alert(t('editor.xmlExportRequiresTeam'));
                          navigate('/pricing');
                        } else {
                          handleExport('xml');
                        }
                      }}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.xml')}</span>
                      {!subscription?.features.exportFormats?.includes('xml') && (
                        <Crown className="w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        const available = subscription?.features.exportFormats?.includes('rtf');
                        if (!available) {
                          alert(t('editor.rtfExportRequiresTeam'));
                          navigate('/pricing');
                        } else {
                          handleExport('rtf');
                        }
                      }}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <span>{t('export.rtf')}</span>
                      {!subscription?.features.exportFormats?.includes('rtf') && (
                        <Crown className="w-3 h-3 text-yellow-500" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 flex items-center border-b border-red-300 dark:border-red-700">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={clearError}
              className="ml-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success/Info Message Display */}
        {message.text && (
          <div className={`p-4 flex items-center border-b ${
            message.type === 'success'
              ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-200 border-green-300 dark:border-green-700'
              : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-200 border-blue-300 dark:border-blue-700'
          }`}>
            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="flex-1">{message.text}</span>
            <button
              onClick={() => setMessage({ text: '', type: '' })}
              className={message.type === 'success'
                ? 'ml-3 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200'
                : 'ml-3 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Editor */}
          <div className="flex-1 flex flex-col">
            <DetachableEditor
              title={title}
              content={content}
              onContentChange={setContent}
              onTitleChange={setTitle}
              onSave={handleSave}
              onFormat={handleFormat}
              onImprove={handleImprove}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={undoStack.length > 0}
              canRedo={redoStack.length > 0}
              isSaving={saving}
              isFormatting={isFormatting}
              isImproving={isImproving}
              aiError={aiError}
              onClearError={clearAiError}
              isLoading={loading}
              canUseAIFeature={canUseAIFeature}
            />
          </div>

          {/* Sidebar */}
          <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => setShowComments(!showComments)}
                  variant={showComments ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {t('editor.comments')}
                </Button>
                <Button
                  onClick={() => setShowVersionHistory(!showVersionHistory)}
                  variant={showVersionHistory ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                >
                  <History className="w-4 h-4 mr-2" />
                  {t('common.edit')}
                </Button>
              </div>
            </div>

            {/* Sidebar Content */}
            <div className="flex-1 overflow-y-auto">
              {showComments && documentExists && (
                <div className="p-4">
                  <Comments documentId={id!} />
                </div>
              )}

              {showVersionHistory && documentExists && (
                <div className="p-4">
                  <VersionHistory
                    documentId={id!}
                    currentTitle={title}
                    onRestoreVersion={handleRestoreVersion}
                    onViewVersion={handleViewVersion}
                  />
                </div>
              )}

              {!showComments && !showVersionHistory && (
                <div className="p-4 space-y-6">
                  {/* Templates Section */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">{t('editor.templates')}</h3>
                    <div className="space-y-2">
                      {templates.slice(0, 3).map((template) => (
                        <button
                          key={template.id}
                          onClick={() => {
                            console.log('=== SIDEBAR TEMPLATE CLICKED ===');
                            console.log('Template object:', template);
                            console.log('Template content:', template.content);
                            console.log('Content type:', typeof template.content);
                            
                            // Use centralized content extraction
                            const templateContent = extractTemplateContent(template.content);
                            
                            console.log('Final extracted content length:', templateContent.length);
                            console.log('Content preview:', templateContent.substring(0, 100));
                            
                            if (templateContent) {
                              setTitle(template.name);
                              setContent(templateContent);
                              setDocumentExists(false);
                              setSaveStatus(null);
                              setError(null);
                              setLoadError(null);
                              console.log('✅ Sidebar template loaded successfully');
                            } else {
                              console.error('❌ Sidebar: No content found in template');
                              setError(t('editor.templateEmpty'));
                            }
                          }}
                          className="w-full p-3 text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {template.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {template.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Formatting */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">{t('editor.quickFormatting')}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => addFormatting('heading')}
                        variant="outline"
                        size="sm"
                        className="font-bold"
                      >
                        <Type className="w-4 h-4 mr-2" />
                        {t('editor.heading')}
                      </Button>
                      <Button
                        onClick={() => addFormatting('bullet')}
                        variant="outline"
                        size="sm"
                        className="font-bold"
                      >
                        <List className="w-4 h-4 mr-2" />
                        {t('editor.bullet')}
                      </Button>
                      <Button
                        onClick={() => addFormatting('number')}
                        variant="outline"
                        size="sm"
                        className="font-bold"
                      >
                        <Hash className="w-4 h-4 mr-2" />
                        {t('editor.number')}
                      </Button>
                      <Button
                        onClick={() => addFormatting('quote')}
                        variant="outline"
                        size="sm"
                        className="font-bold"
                      >
                        <Quote className="w-4 h-4 mr-2" />
                        {t('editor.quote')}
                      </Button>
                    </div>
                  </div>

                  {/* Quick Fill */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">{t('editor.quickFill')}</h3>
                    <QuickFillButton
                      onFill={handleQuickFill}
                      onReplace={handleQuickFillReplace}
                      documentContent={content}
                      disabled={loading}
                    />
                  </div>

                  {/* AI Tools */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">{t('editor.aiTools')}</h3>
                    <div className="space-y-2">
                      <AIFeatureGate requiredLevel="advanced">
                        <Button
                          onClick={() => setShowChatWidget(true)}
                          variant="outline"
                          className="w-full font-bold"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          {t('editor.aiAssistant')}
                        </Button>
                      </AIFeatureGate>
                      
                      <AIFeatureGate requiredLevel="advanced">
                        <Button
                          onClick={handleImprove}
                          disabled={isImproving || loading}
                          variant="outline"
                          className="w-full font-bold"
                        >
                          {isImproving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t('editor.improving')}
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4 mr-2" />
                              {t('editor.improve')}
                            </>
                          )}
                        </Button>
                      </AIFeatureGate>
                      
                      <AIFeatureGate requiredLevel="basic">
                        <Button
                          onClick={handleFormat}
                          disabled={isFormatting || loading}
                          variant="outline"
                          className="w-full font-bold"
                        >
                          {isFormatting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t('editor.formatting')}
                            </>
                          ) : (
                            <>
                              <Type className="w-4 h-4 mr-2" />
                              {t('editor.format')}
                            </>
                          )}
                        </Button>
                      </AIFeatureGate>

                      <AIFeatureGate requiredLevel="basic">
                        <Button
                          onClick={() => setShowSummarizer(true)}
                          disabled={loading || !content.trim()}
                          variant="outline"
                          className="w-full font-bold"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          {t('editor.summarize')}
                        </Button>
                      </AIFeatureGate>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs and Widgets */}
      {showShareDialog && documentExists && (
        <ShareDialog
          documentId={id!}
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
        />
      )}

      {showChatWidget && (
        <ChatWidget
          onClose={() => setShowChatWidget(false)}
          documentContent={content}
          onApplySuggestion={handleApplySuggestion}
          canUseAIFeature={canUseAIFeature}
        />
      )}

      {showSummarizer && (
        <DocumentSummarizer
          documentContent={content}
          isOpen={showSummarizer}
          onClose={() => setShowSummarizer(false)}
        />
      )}
    </div>
  );
};

export default DocumentEditor;